import { Stack, Typography } from "@mui/material";
import type { useGridApiContext } from "@mui/x-data-grid";
import {
  type GridCsvExportOptions,
  type GridCsvGetRowsToExportParams,
  gridExpandedSortedRowIdsSelector,
} from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { BaseButton } from "shared/atoms/BaseButton";

interface Props {
  actions?: React.ReactNode;
  apiRef: ReturnType<typeof useGridApiContext>;
  bulkActions?: React.ReactNode;
  count: number;
  csvFilename?: string;
  itemLabel: string;
  onOpenColumns?: () => void;
  onOpenFilter?: () => void;
  showColumnsButton?: boolean;
  showExportCsvButton?: boolean;
  showFilterButton?: boolean;
}

const getFilteredRows = ({ apiRef }: GridCsvGetRowsToExportParams) => gridExpandedSortedRowIdsSelector(apiRef);

export function GridToolbar({
  actions,
  apiRef,
  bulkActions,
  count,
  csvFilename,
  itemLabel,
  onOpenColumns,
  onOpenFilter,
  showColumnsButton = true,
  showExportCsvButton = true,
  showFilterButton = false,
}: Props) {
  const { t } = useTranslation();

  const handleExport = (options: GridCsvExportOptions) => apiRef.current.exportDataAsCsv(options);

  return (
    <ToolbarRow>
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="body1" style={{ fontWeight: 600 }}>
          {count} {itemLabel}
        </Typography>
        {bulkActions && bulkActions}
      </Stack>

      <ToolbarRight>
        {actions}
        {showFilterButton && onOpenFilter && (
          <BaseButton variant="action" onClick={onOpenFilter} icon="sliders-simple" text={t("form.filter")} />
        )}
        {showColumnsButton && onOpenColumns && (
          <BaseButton variant="action" onClick={onOpenColumns} icon="columns-3" text={t("form.columns")} />
        )}
        {showExportCsvButton && apiRef && (
          <BaseButton
            variant="action"
            onClick={() => handleExport({ getRowsToExport: getFilteredRows, fileName: csvFilename })}
            icon="arrow-down-from-bracket"
            text={t("buttons.export_csv")}
          />
        )}
      </ToolbarRight>
    </ToolbarRow>
  );
}

const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0 0 16px;
  width: 100%;
`;

const ToolbarRight = styled.div`
  margin-left: auto;
  display: flex;
  gap: 8px;
`;
